import { useState, type KeyboardEvent } from 'react';

import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import { useWallStore, type WallBoard } from '@walls/store';
import {
  getCustomHeaderTitle,
  getTitleBackgroundColor,
  getTitleColor,
} from '../theme/wallThemeStyles';

interface WorkspaceHeaderProps {
  board: WallBoard;
}

export function WorkspaceHeader({ board }: WorkspaceHeaderProps) {
  const [isTitleHidden, setIsTitleHidden] = useState(false);
  const theme = useWallStore((state) => state.theme);
  const titleBackgroundColor = getTitleBackgroundColor(theme);
  const titleColor = getTitleColor(theme);
  const customHeaderTitle = getCustomHeaderTitle(theme);

  const workspaceHref = board.workspaceExternalId
    ? `https://workspace.dotstorming.com/${board.workspaceExternalId}`
    : board.workspace
    ? `/main?workspaceId=${board.workspace}`
    : '/main';

  const hideTitle = () => {
    setIsTitleHidden(true);
  };

  const hideTitleFromKeyboard = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      hideTitle();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        alignItems: 'center',
        bgcolor: titleBackgroundColor,
        borderRadius: '8px',
        boxShadow: '0px 3px 6px #00000029',
        display: 'flex',
        height: 50,
        left: { xs: 10, sm: 20 },
        maxWidth: { xs: 'calc(100vw - 86px)', sm: 'calc(100vw - 220px)' },
        overflow: 'hidden',
        position: 'fixed',
        top: { xs: 10, sm: 20 },
        px: 1,
        zIndex: 1000,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
        {board.includeWorkspaceLink ? (
          <>
            <Button
              component="a"
              href={workspaceHref}
              color="inherit"
              sx={{ color: titleColor, fontWeight: 700, height: '100%', px: 2 }}
            >
              {board.workspaceName}
            </Button>
            <Typography
              variant="body2"
              component="h1"
              noWrap
              sx={{ color: titleColor, fontWeight: 700, px: 2 }}
            >
              {board.title}
            </Typography>
          </>
        ) : (
          <>
            <Box
              component="a"
              href={workspaceHref}
              sx={{
                alignItems: 'center',
                borderRight: isTitleHidden ? 'none' : '2px solid #f3f5f8',
                color: titleColor,
                display: 'inline-flex',
                height: '100%',
                px: 2,
                py: 1,
              }}
            >
              {customHeaderTitle ? (
                <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 700 }}>
                  {customHeaderTitle}
                </Typography>
              ) : (
                <Box
                  component="img"
                  alt="dotstorming"
                  src="https://storage.googleapis.com/dotstorming-img/logo_small.svg"
                  height={30}
                />
              )}
            </Box>
            {!isTitleHidden && (
              <Typography
                variant="body2"
                component="h1"
                noWrap
                onClick={hideTitle}
                onKeyDown={hideTitleFromKeyboard}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  color: titleColor,
                  display: { xs: 'none', sm: 'block' },
                  px: 2,
                }}
              >
                {board.title}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
